import { ChangeEvent, SyntheticEvent, useContext, useState } from "react"
import { InputText } from "primereact/inputtext"
import FieldError, { FormErrors } from "../../FieldError"
import { ChangePasswordPayload } from "../../../api/types/user"
import { classNames } from "primereact/utils"
import commonStyles from "../../common.module.css"
import { Button } from "primereact/button"
import userContext from "../../../context/user"
import { useChangePassword } from "../../../api/queries/useChangePassword"
import globalToastContext from "../../../context/globalToast"
import { ResponseData } from "../../../api/types/base"

export default function ChangePasswordForm() {
  const { user, logout } = useContext(userContext)
  const { ref } = useContext(globalToastContext)
  const changePassword = useChangePassword(user?.uuid)

  const [oldPassword, setOldPassword] = useState<string>("")
  const handleOldPasswordChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setOldPassword(event.target.value)
  }

  const [newPassword, setNewPassword] = useState<string>("")
  const handleNewPasswordChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setNewPassword(event.target.value)
  }

  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState<boolean>(false)

  const [newPasswordConfirm, setNewPasswordConfirm] = useState<string>("")
  const handleNewPasswordConfirmChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setNewPasswordConfirm(event.target.value)
  }

  const [errors, setErrors] = useState<FormErrors<FormValue>>()

  const validate = (): [boolean, FormErrors<FormValue>] => {
    const formErrors: FormErrors<FormValue> = {}

    if (!oldPassword) {
      formErrors.oldPassword = "Staré heslo je povinné pole."
    }

    if (!newPassword) {
      formErrors.newPassword = "Nové heslo je povinné pole."
    }

    if (!newPasswordConfirm) {
      formErrors.newPasswordConfirm = "Potvrzení nového hesla je povinné pole."
    }

    if (newPassword !== newPasswordConfirm) {
      formErrors.newPasswordConfirm = "Hesla se neshodují."
    }

    return [Object.keys(formErrors).length === 0, formErrors]
  }

  const handleFormSubmit = async (event: SyntheticEvent) => {
    event.preventDefault()

    const payload: ChangePasswordPayload = {
      oldPassword,
      newPassword,
    }

    const [isValid, errors] = validate()
    setErrors(errors)

    if (isValid) {
      try {
        await changePassword.mutateAsync(payload)
        ref?.current?.show({ severity: "success", summary: "Heslo změněno." })
        logout()
      } catch (err: any) {
        const response: ResponseData = err.response.data
        const newErrors: FormErrors<FormValue> = {}

        response.errors.forEach((error) => {
          newErrors[error.property as keyof FormValue] = error.message
        })

        setErrors(newErrors)
      }
    }
  }

  return (
    <form className={"col-12"} onSubmit={handleFormSubmit}>
      <div className={"p-fluid"}>
        <div className={"block-title block-title_blue px-3 py-2 border-round-top"}>
          Změna hesla
        </div>

        <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={oldPassword} onChange={handleOldPasswordChange}
                  type={"password"}
                  className={classNames({ "p-invalid": !!errors?.oldPassword })} />
                <label className={commonStyles.labelRequired}>Staré heslo:</label>
              </span>
              <FieldError error={errors?.oldPassword} />
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <div className={"p-inputgroup"}>
                <span className={"p-float-label"}>
                  <InputText value={newPassword} onChange={handleNewPasswordChange}
                    type={isNewPasswordVisible ? "text" : "password"}
                    className={classNames({ "p-invalid": !!errors?.newPassword })} />
                  <label className={commonStyles.labelRequired}>Nové heslo:</label>
                </span>
                <Button type={"button"} icon={`pi pi-${isNewPasswordVisible ? "eye-slash" : "eye"}`}
                  className={"p-button-secondary"}
                  onClick={() => setIsNewPasswordVisible(!isNewPasswordVisible)} />
              </div>
              <span className={commonStyles.fieldHelp}>Minimální délka hesla je 6 znaků.</span> <FieldError error={errors?.newPassword} />
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={newPasswordConfirm} onChange={handleNewPasswordConfirmChange}
                  type={"password"}
                  className={classNames({ "p-invalid": !!errors?.newPasswordConfirm })} />
                <label className={commonStyles.labelRequired}>Potvrzení nového hesla:</label>
              </span>
              <FieldError error={errors?.newPasswordConfirm} />
            </div>
          </div>
        </div>
      </div>

      <div className={"text-right"}>
        <Button type={"submit"} label={"Odeslat"}
          className={"p-button p-button-sm p-button-success"}
          icon={"pi pi-save"}
          disabled={changePassword.isLoading} />
      </div>
    </form>
  )
}

interface FormValue {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
}
